import { DetailBox, Icon } from '@octano/global-ui';
import { Card, Col, Row, CardBody, FormText } from 'reactstrap';
import GoBackToListButton from '../../../../../components/buttons/GoBackToListButton';
import { useDebtDetailLoader } from './DebtDetailLoader';
import Loading from '../../../../../components/info/Loading';
import Tooltip from '../../../../../components/tooltip/Tooltip';
import { useTranslation } from 'react-i18next';

const formatter = (value: number) =>
  `$${value.toLocaleString('es-CL', {
    style: 'decimal',
    currency: 'CLP',
  })}`;

const dictPrefix = 'financialPortal.currentAccount.tabs.currentAccount.detail';

function DebtDetailCard() {
  const { data, loading } = useDebtDetailLoader();
  const { t } = useTranslation();

  if (loading || !data) {
    return (
      <div className="d-flex justify-content-center">
        <Loading />
      </div>
    );
  }

  const {
    studentName,
    rutOrPassport,
    concept,
    numberOfInstallment,
    expirationDate,
    daysLate,
    interestRate,
    capitalAmount,
    interestAmount,
    totalAmount,
    paidAmount,
    outstandingDebt,
  } = data;

  const CardItem = ({
    title,
    content,
    paddingTop = '13px',
    paddingRight = '20px',
    contentSize = '16px',
    contentColor = '',
    tooltip = '',
    minContentWidth = '80px',
  }: {
    title: string;
    content: string;
    paddingTop?: string;
    paddingRight?: string;
    contentSize?: string;
    contentColor?: string;
    tooltip?: string;
    minContentWidth?: string;
  }) => {
    return (
      <div className="d-flex flex-column" style={{ paddingTop, paddingRight }}>
        <p className="text-muted m-0" style={{ fontSize: '14px' }}>
          {title}
        </p>
        <FormText
          className="font-weight-bold m-0"
          style={{
            fontSize: contentSize,
            display: 'flex',
            gap: '10px',
            maxWidth: '250px',
            minWidth: minContentWidth,
          }}
          color={contentColor}
        >
          {content}
          {tooltip && (
            <Tooltip id={`info_tooltip`} placement="right" text={tooltip}>
              <Icon name="information" size={14.71} />
            </Tooltip>
          )}
        </FormText>
      </div>
    );
  };

  return (
    <Card className="p-4 mx-3">
      <Row>
        <Col className="mb-4" xs={12}>
          <div className="d-flex flex-wrap w-100 align-items-start justify-content-center">
            <GoBackToListButton />
            <div className="flex-fill px-2" />
            <Col>
              <DetailBox
                columns={[
                  { title: t(`${dictPrefix}.studentName`), body: studentName },
                  { title: t(`${dictPrefix}.studentId`), body: rutOrPassport },
                ]}
              />
            </Col>
          </div>
        </Col>
        <Col>
          <Card className="border border-primary p-2" style={{ width: '80%' }}>
            <div className="px-3 pt-3">
              <p className="fs-20 font-weight-bold m-0">
                {t(`${dictPrefix}.cardTitle`)}
              </p>
            </div>
            <CardBody>
              <div className="d-flex flex-row flex-wrap gap-4">
                <CardItem
                  title={t(`${dictPrefix}.outstandingDebt`)}
                  content={formatter(outstandingDebt)}
                  paddingRight="50px"
                  contentSize="30px"
                  paddingTop="0"
                  contentColor="primary"
                  minContentWidth="120px"
                />
                <CardItem
                  title={t(`${dictPrefix}.concept`)}
                  content={concept}
                  contentSize={'14px'}
                  minContentWidth="250px"
                />
                <CardItem
                  title={t(`${dictPrefix}.installmentNumber`)}
                  content={numberOfInstallment}
                />
                <CardItem
                  title={t(`${dictPrefix}.capitalAmount`)}
                  content={formatter(capitalAmount)}
                />
                <CardItem
                  title={t(`${dictPrefix}.totalAmount`)}
                  content={formatter(totalAmount)}
                />
                <CardItem
                  title={t(`${dictPrefix}.interestAmount`)}
                  content={formatter(interestAmount)}
                />

                <div className="w-100 pb-4"></div>

                <CardItem
                  title={t(`${dictPrefix}.paidAmount`)}
                  content={formatter(paidAmount)}
                  paddingRight="50px"
                  contentSize="30px"
                  paddingTop="0"
                  contentColor="primary"
                  minContentWidth="120px"
                />
                <CardItem
                  title={t(`${dictPrefix}.interestRate`)}
                  content={interestRate + '%'}
                  minContentWidth="250px"
                />
                <CardItem
                  title={t(`${dictPrefix}.expirationDate`)}
                  content={expirationDate.replaceAll('-', '/')}
                />
                <CardItem
                  title={t(`${dictPrefix}.daysLate`)}
                  content={`${daysLate} ${t(`${dictPrefix}.daysSuffix`)}`}
                />
                <CardItem
                  title={t(`${dictPrefix}.cosigner`)}
                  content={t(`${dictPrefix}.cosignerPlaceholder`)}
                  tooltip={t(`${dictPrefix}.cosignerTooltip`)}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Card>
  );
}

export default DebtDetailCard;
